import { useMemo } from "react";
import {
  PayeeRow,
  ResponseError,
  WorkLog,
  WorkLogList,
  WorkLogsApiListWorkLogsRequest
} from "@wingspanhq/wingspan-api-sdk";
import { WSInfiniteQueryConfig } from "@ws-react-query";
import { queriesPayeeRows, queriesWorkLogs } from "@query";

export type WorkLogWithPayee = WorkLog & { payee?: PayeeRow };

/**
 * v3 reimplementation of the composed read. Pages of work logs come from the v3
 * `listWorkLogs` registry hook; the optional payee join resolves each work log's
 * contractor from `searchPayeeRows`.
 *
 * The payee-row search cannot batch-filter by `payeeEngagementId` (that filter
 * only exposes `eq`), so the join fetches the caller's full payee set once and
 * indexes every engagement to its owning row. `PayeeRow.id` is the row id — NOT
 * the engagement id — and a single row can hold several engagements, so the map
 * is keyed on each `engagements[].payeeEngagementId` (matches the `eq` join the
 * work-log detail route already uses).
 */
export const useQueryWorkLogsWithPayees = (
  requestQuery?: WorkLogsApiListWorkLogsRequest,
  config?: {
    joinPayee?: boolean;
  } & WSInfiniteQueryConfig<WorkLogList, ResponseError>
) => {
  const joinPayee = config?.joinPayee || false;

  const query = queriesWorkLogs.listWorkLogs.usePaginatedQuery(
    requestQuery,
    config
  );

  const workLogs = query.flatData;

  const queryPayees = queriesPayeeRows.searchPayeeRows.useQueryAll(undefined, {
    enabled: joinPayee && workLogs.length > 0
  });

  const payees = queryPayees.flatData;

  const payeeByEngagementId = useMemo(() => {
    const map = new Map<string, PayeeRow>();
    payees.forEach(payee => {
      payee.engagements?.forEach(engagement => {
        if (engagement.id) {
          map.set(engagement.id, payee);
        }
      });
    });
    return map;
  }, [payees]);

  const data = useMemo<WorkLogWithPayee[]>(
    () =>
      workLogs.map(workLog => ({
        ...workLog,
        payee:
          joinPayee && workLog.payeeEngagementId
            ? payeeByEngagementId.get(workLog.payeeEngagementId)
            : undefined
      })),
    [workLogs, payeeByEngagementId, joinPayee]
  );

  return { ...query, data, flatData: data };
};
